import qs from 'qs';
import { useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation, matchRoutes, RouteMatch } from 'react-router-dom';
import { RedirectToRootRoute } from './RedirectToRootRoute';
import { AppointmentDispatchTypes, AppointmentProvider, useAppointmentDispatch, useAppointmentStateUnsafe } from '../../context/Appointment/appointment';
import { CartProvider } from '../../context/cart';
import { AppointmentIndex } from '../Appointment/AppointmentIndex';
import { CustomerIndex } from '../Customer/CustomerIndex';
import { Home } from '../Home';
import { ContinueSavedSession } from '../SaveSession/ContinueSavedSession';
import { SaveSession } from '../SaveSession/SaveSession';
import { VehiclePickUpRoutes } from '../VehiclePickUpRoutes/VehiclePickUpRoutes';
import { DealerLanding } from '../Appointment/DealerLanding';
import { OutdoorLanding } from '../Outdoor/OutdoorLanding';
import { useKioskState, useKioskDispatch, useAdditionalDealers, useSwitchDealer } from '../../context/Kiosk/kiosk';
import { OutdoorPickup } from '../Outdoor/Pickup';
import { OutdoorPickUpIndex } from '../Appointment/PickUp/Outdoor/OutdoorPickUpIndex';
import { AccessibilityDialog } from '../AccessibilityDialog/AccessibilityDialog';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { OnlinePaymentIndex } from '../OnlinePayment';
import { AppRoutePaths, AppointmentRoutePaths, PathName } from '../../helpers/Routes';
import { CheckInDetailsIndex } from '../CheckInDetails';
import { PickupQRCodeIndex } from '../PickupQRCode';
import type { ConfirmInformationQueryParams } from '../Appointment/ConfirmInformation/ConfirmInformation.types';
import type { GoMotoComponent } from '../../API/GoMoto.types';

export const Index: GoMotoComponent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const appointmentDispatch = useAppointmentDispatch();
    const appointment = useAppointmentStateUnsafe();
    const isOutdoorLanding: boolean = location.pathname === AppRoutePaths.ImHere;
    const suppressRecallRoutes: RouteMatch[] | null = matchRoutes(
        [
            {
                path: '/continue-session/*',
            },
            {
                path: '/landing/*',
            },
            {
                path: '/vehicle-pick-up/*',
            },
            {
                path: '/outdoor-vehicle-pick-up/*',
            },
        ],
        location,
    );

    const suppressRecalls: boolean = !!suppressRecallRoutes && suppressRecallRoutes.length > 0;
    const { kioskType, dealer } = useKioskState();
    const additionalDealers = useAdditionalDealers();
    const kioskDispatch = useKioskDispatch();
    const switchDealer = useSwitchDealer();
    const checkInRouteEnabled: boolean = isElectron() || (!isElectron() && dealer.mobileCheckInEnabled);

    useEffect(() => {
        if (!isOutdoorLanding) {
            const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
            let apptKey = '';
            let isDmsApptKey = false;
            if ('dmsApptKey' in parsed && typeof parsed.dmsApptKey === 'string') {
                apptKey = parsed.dmsApptKey;
                isDmsApptKey = true;
                if ('dealerKey' in parsed && typeof parsed.dealerKey === 'string') {
                    const newDealer = additionalDealers.find((i) => i.key === parsed.dealerKey);
                    newDealer && newDealer.key !== dealer.key && switchDealer(newDealer);
                }
            }
            else if ('apptkey' in parsed && typeof parsed.apptkey === 'string') {
                apptKey = parsed.apptkey;
            }
            if (apptKey) {
                if (!appointment || (!isDmsApptKey && appointment.key !== apptKey) || (isDmsApptKey && appointment.dmsAppointmentKey !== apptKey)) {
                    appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
                    const confirmParams: ConfirmInformationQueryParams = {
                        ...(isDmsApptKey ? { isDmsApptKey: 'true' } : {}),
                    };
                    navigate(`${PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Confirm, apptKey)}${qs.stringify(confirmParams, { addQueryPrefix: true })}`);
                }
                else {
                    appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
                    navigate('/', { replace: true });
                }
            }
        }
    }, [location.search, navigate, appointment, appointmentDispatch, isOutdoorLanding, additionalDealers, kioskDispatch, dealer, switchDealer]);

    return (
        <AppointmentProvider useCache suppressRecallCalls={!!suppressRecalls}>
            <CartProvider useCache>
                <Routes>
                    <Route
                        path={PathName(AppRoutePaths.Landing, ':returnPage', '*')}
                        element={
                            <RedirectToRootRoute loaded={checkInRouteEnabled}>
                                <DealerLanding
                                    onProceed={(returnPage) => navigate(`/${returnPage}`)}
                                    onSkip={(returnPage) => navigate(`/${returnPage}`, { replace: true })}
                                />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.Appointment, '*')}
                        element={
                            <RedirectToRootRoute loaded={checkInRouteEnabled}>
                                <AppointmentIndex />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.Customer, '*')}
                        element={
                            <RedirectToRootRoute loaded={checkInRouteEnabled}>
                                <CustomerIndex />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.ContinueSession, '*')}
                        element={
                            <RedirectToRootRoute loaded={checkInRouteEnabled}>
                                <ContinueSavedSession />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.SaveSession, '*')}
                        element={
                            <RedirectToRootRoute loaded={checkInRouteEnabled}>
                                <SaveSession />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.Accessibility, '*')}
                        element={
                            <RedirectToRootRoute loaded={!isElectron()}>
                                <AccessibilityDialog />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.VehiclePickUp, '*')}
                        element={
                            <RedirectToRootRoute loaded>
                                <VehiclePickUpRoutes />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.OutdoorPickUp, '*')}
                        element={
                            <RedirectToRootRoute loaded>
                                <OutdoorPickup />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={AppRoutePaths.ImHere}
                        element={
                            <RedirectToRootRoute loaded>
                                <OutdoorLanding />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.OutdoorVehiclePickUp, '*')}
                        /*
                         * TODO: This leads to the Appointment/PickUp/Outdoor path. Since it doesn't use any appointment routing,
                         * all of the child components should be in it's own component folder.
                         */
                        element={
                            <RedirectToRootRoute loaded={kioskType === 'outdoor'}>
                                <OutdoorPickUpIndex />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.OnlinePayment, '*')}
                        element={
                            <RedirectToRootRoute loaded>
                                <OnlinePaymentIndex />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.CheckInDetails, '*')}
                        element={
                            <RedirectToRootRoute loaded>
                                <CheckInDetailsIndex />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route
                        path={PathName(AppRoutePaths.PickupQRCode, '*')}
                        element={
                            <RedirectToRootRoute loaded>
                                <PickupQRCodeIndex />
                            </RedirectToRootRoute>
                        }
                    />
                    <Route path={'*'} element={<Home />} />
                </Routes>
            </CartProvider>
        </AppointmentProvider>
    );
};